import OrderAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity";
import { SelectionModel } from "@angular/cdk/collections";
import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { IUpdateOrderUnified } from "app/modules/components/sales/update-order/interfaces/update-order.interface";
import { LoadsService } from "../../loads.service";
import { validateOrdersByAction } from "app/utils/document-class-validation";
import { OrdersListService } from "app/modules/components/sales/order-list-components/order-list.service";
import DocumentClassAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/documents/document-class/document-class.entity";
import { MatMenuModule } from "@angular/material/menu";
import { Router } from "@angular/router";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { DocumentNavigationComponent } from "app/common/components/document-navigation/document-navigation.component";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { of, delay } from "rxjs";
import { UnitMeasureDisplayComponent } from "app/shared/components/unit-measure-display/unit-measure-display.component";
import { ConflictModalComponent } from "app/shared/components/order-unify-modal/order-unify-modal.component";

@Component({
  selector: "app-material-history",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    MatIcon,
    MatMenuModule,
    MatTooltipModule,
    MatCheckboxModule,
    DocumentNavigationComponent,
    UnitMeasureDisplayComponent,
  ],
  templateUrl: "./material-history.component.html",
  styleUrl: "./material-history.component.scss",
})

// TODO: Separar la lógica de este componente en dos componentes distintos:
// 1. MaterialHistoryComponent - Para mostrar historial de materiales/órdenes (usado en summary-material.component)
// 2. PaymentHistoryComponent - Para mostrar historial de pagos (usado en monitor-routes.component)
// Actualmente este componente maneja ambos casos usando isPaymentModal flag, pero sería mejor tener componentes separados
export class MaterialHistoryComponent implements OnInit, OnChanges {
  @Input() element: any;
  @Input() UMPActive: boolean = false;

  @Output() refreshMonitor = new EventEmitter<void>();

  @Output() onSortChange: EventEmitter<{
    active: string;
    direction: "ASC" | "DESC";
  }> = new EventEmitter();

  displayedColumnsHistoric: string[] = [
    "check",
    "serial",
    "documentClass",
    "soldTo",
    "shipTo",
    "quantities",
    "actions",
  ];

  // Columns for payment context
  displayedColumnsPayment: string[] = [
    "documentSerial",
    "payer",
    "destinatary",
    "paymentType",
    "overpayment",
    "amount",
    "total",
  ];

  dataSourceHistoric = new MatTableDataSource<any>([]);
  documentClassesList: DocumentClassAttributes[] = [];

  documentTypeEnum = DocumentTypeEnum;

  selection = new SelectionModel<any>(true, []);
  selectedOrder: OrderAttributes | null = null;

  // Property to detect payment context
  get isPaymentContext(): boolean {
    return this.data?.hideCheckColumn === true;
  }

  // Property to detect payment modal context
  get isPaymentModal(): boolean {
    return this.data?.isPaymentModal === true;
  }

  //VARIABLES PARA UNIFICAR
  isNoOrderSelectionJoin: boolean = false;
  canJoin: boolean = true;

  @ViewChild("confirmDialog") confirmDialog!: TemplateRef<any>;
  constructor(
    private readonly snackBar: MatSnackBar,
    private loadService: LoadsService,
    private readonly dialog: MatDialog,
    private readonly ordersListService: OrdersListService,
    private readonly router: Router,
    public dialogRef: MatDialogRef<MaterialHistoryComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    // Check if this is a payment modal
    if (this.isPaymentModal) {
      // For payment modal, use payment columns and payment data
      this.dataSourceHistoric = new MatTableDataSource<any>(
        this.data?.element?.payments ?? []
      );
      // Don't fetch document classes for payment modal
    } else {
      // Original logic for order modal
      // Ocultar "check" si hideCheckColumn es true esto para tipos de pago
      if (this.data?.hideCheckColumn) {
        this.displayedColumnsHistoric = this.displayedColumnsHistoric.filter(
          (col) => col !== "check"
        );
      }

      this.dataSourceHistoric = new MatTableDataSource<any>(
        this.data?.element?.orders ?? []
      );
      this.fetchDocumentClass();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.element && changes.element.currentValue) {
      if (this.isPaymentModal) {
        this.dataSourceHistoric.data = this.data?.element?.payments ?? [];
      } else {
        this.dataSourceHistoric.data = this.data?.element?.orders ?? [];
        this.fetchDocumentClass(); // si necesitas validar nuevamente
      }
    }
  }

  /**
   * Get total amount for payment context
   * @param historic - The order/historic data or payment data
   * @returns The total amount for the line/detail or payment amount
   */
  getTotalAmount(historic: any): number {
    if (this.isPaymentModal) {
      // For payment modal, return the payment amount directly
      return historic?.paying ?? historic?.amount ?? 0;
    }

    if (this.isPaymentContext) {
      // Get the first detail using the existing method
      const detail = this.getFirstDetail(historic);

      if (detail) {
        // Return the line total amount from the detail
        return (
          detail?.totals?.totalAmount ??
          detail?.totalAmount ??
          detail?.lineTotal ??
          detail?.total ??
          0
        );
      }
    }

    // Fallback: calculate from order totals
    return historic?.totals?.totalAmount ?? historic?.totalAmount ?? 0;
  }

  validateOrdersByAction(order: OrderAttributes): boolean {
    return validateOrdersByAction([order], this.documentClassesList, "JOIN");
  }

  isAllSelected() {
    const validRows = this.dataSourceHistoric.data.filter((row) =>
      this.validateOrdersByAction(row)
    );

    const numSelected = this.selection.selected.length;
    const numValidRows = validRows.length;
    return numSelected == numValidRows && numValidRows > 0;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSourceHistoric.data.forEach((row) => {
          if (this.validateOrdersByAction(row)) this.selection.select(row);
        });
  }

  onCheckboxChange(event, element?): void {
    let auxSelected = this.selection.selected;
    // Nueva validación para unificación
    this.isNoOrderSelectionJoin =
      auxSelected.length < 2 ||
      auxSelected.filter((order) => order.canJoin).length < 2;
  }

  openConfirmSDialog(message: string): void {
    this.dialog
      .open(this.confirmDialog, {
        data: { title: "Advertencia", message: message },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.isNoOrderSelectionJoin = true;
        }
      });
  }

  openMConfirmDialog(): void {
    const ordersToJoinFiltered = this.selection.selected.filter(
      (order) => order.canJoin
    );
    if (ordersToJoinFiltered.length == 0) {
      this.openConfirmSDialog(
        "Los pedidos seleccionados no están permitidos para unificarse."
      );
      return;
    }
    this.dialog
      .open(this.confirmDialog, {
        data: {
          title: "Confirmar",
          innerHTML: `
              <p>Los siguientes pedidos pueden ser unificados:</p>
              <ul>
                ${ordersToJoinFiltered.map((order) => `<li>${order.serial}</li>`).join("")}
              </ul>
              <p>¿Está seguro que desea unificarlos?</p>
            `,
        },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.validateJoinOrderModal(ordersToJoinFiltered);
        }
      });
  }

  validateJoinOrderModal(ordersToJoinFiltered: OrderAttributes[] | any) {
    if (this.selection.selected.length == 0) {
      return;
    }

    const allCanUnify = ordersToJoinFiltered.every(
      (order) => order.canUnifyConditions
    );
    const uniqueClass = new Set(
      ordersToJoinFiltered.map((order) => order.class?._id || "sin_class")
    );
    const materialMap = new Map<string, Set<number>>();
    ordersToJoinFiltered.forEach((order) => {
      const details = Array.isArray(order.detail)
        ? order.detail
        : [order.detail].filter(Boolean);

      details.forEach((item) => {
        const materialId = item.material?._id || ("sin_item" as any);
        const unitPrice = item.unitPrice;

        if (!materialMap.has(materialId)) {
          materialMap.set(materialId, new Set());
        }
        materialMap.get(materialId)?.add(unitPrice);
      });
    });

    let message = "";
    let hasPriceM = false;

    materialMap.forEach((prices, materialId) => {
      if (prices.size > 1) {
        hasPriceM = true;
      }
    });

    if (!allCanUnify) {
      if (uniqueClass.size > 1 && hasPriceM) {
        message =
          "Las órdenes seleccionadas pertenecen a diferentes clases y tienen variaciones en los precios de los materiales.";
        this.openConfirmSDialog(message);
      } else if (uniqueClass.size > 1) {
        message = "Las órdenes seleccionadas pertenecen a diferentes clases.";
        this.openConfirmSDialog(message);
      } else if (hasPriceM) {
        message =
          "Las órdenes seleccionadas tienen variaciones en los precios de los materiales.";
        this.openConfirmSDialog(message);
      }
    }
    this.joinOrders(ordersToJoinFiltered);
  }

  // joinOrdersMock(ordersToJoinFiltered: OrderAttributes[]): void {
  //   let send: IUpdateOrderUnified = {
  //     orderIds: ordersToJoinFiltered.map((order) => String(order._id)),
  //   };

  //   // Simulación de servicio con delay de 1 segundo
  //   of(send)
  //     .pipe(delay(1000))
  //     .subscribe({
  //       next: () => {
  //         this.snackBar.open(
  //           "Registro unificados (simulado) con éxito",
  //           "Cerrar",
  //           {
  //             duration: 3000,
  //           }
  //         );
  //         if (this.selection.selected.length > 0) this.selection.clear();
  //         this.refreshMonitor.emit();
  //         this.dialogRef.close();
  //       },
  //       error: () => {
  //         this.snackBar.open(
  //           "Error simulado al actualizar registro",
  //           "Cerrar",
  //           { duration: 3000 }
  //         );
  //       },
  //     });
  // }

  joinOrders(ordersToJoinFiltered: OrderAttributes[]): void {
    let send: IUpdateOrderUnified = {
      orderIds: ordersToJoinFiltered.map((order) => String(order._id)),
    };

    this.loadService.joinOrder(send).subscribe({
      next: () => {
        this.snackBar.open("Registro unificados con éxito", "Cerrar", {
          duration: 3000,
        });
        if (this.selection.selected.length > 0) this.selection.clear();
        this.refreshMonitor.emit();
        this.dialogRef.close();
      },
      error: (err) => {
        if (err.status === 409 && err.error?.conflicts) {
          this.dialog.open(ConflictModalComponent, {
            width: "700px",
            maxWidth: "95vw",
            data: {
              conflicts: err.error.conflicts,
              allowDiffComercialConds:
                err.error.allowDiffComercialConds || false,
            },
            disableClose: false,
            autoFocus: true,
          });
        } else {
          this.snackBar.open(
            err.error?.message || "Error al unificar documentos",
            "Cerrar",
            { duration: 3000 }
          );
        }
      },
    });
  }

  // Para unificar documentos de carga

  private async validateRequirements(): Promise<void> {
    const dataTemp = this.dataSourceHistoric.data.map((order) => {
      const canJoin = validateOrdersByAction(
        [order],
        this.documentClassesList,
        "JOIN"
      );
      const docClass = this.documentClassesList.find(
        (d) => d._id?.toString() === order.class?._id?.toString()
      );

      return {
        ...order,
        canJoin,
        canUnifyConditions: docClass?.canUnifyConditions ?? false,
      };
    });
    this.dataSourceHistoric.data = dataTemp;
    this.dataSourceHistoric._updateChangeSubscription();
  }

  private fetchDocumentClass(): void {
    let dataToFilter = {
      type: DocumentTypeEnum.PEDIDO,
    };
    this.ordersListService
      .fetchDocumentClasses(dataToFilter)
      .subscribe((res) => {
        this.documentClassesList = res.data;
        this.validateRequirements();
      });
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? "select" : "deselect"} all`;
    }
    return `${this.selection.isSelected(row) ? "deselect" : "select"} row ${row.position + 1}`;
  }

  getFirstDetail(historic: any) {
    return Array.isArray(historic?.detail)
      ? historic?.detail[0]
      : historic?.detail;
  }

  /**
   * Calculate total amount for payment modal
   * @returns Total of all payments
   */
  getTotalPaymentAmount(): number {
    if (!this.isPaymentModal || !this.dataSourceHistoric.data) {
      return 0;
    }

    return this.dataSourceHistoric.data.reduce((total, payment) => {
      return total + this.getTotalAmount(payment);
    }, 0);
  }
}
